import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, type SxProps, type Theme, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DrawerTriggerButton } from "../../components/DrawerTriggerButton/DrawerTriggerButton";
import { DateSlider } from "../../components/dateSlider/DateSlider";
import RecommendationsDrawer from "../../components/RecommendationsDrawer/RecommendationsDrawer";
import { StaffAutoComplete } from "../../components/UIToolkit/Inputs/StaffAutoComplete";
import type { SystemAutoCompleteOption } from "../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { DepartmentTypeChips } from "../../components/UIToolkit/molecules/DepartmentTypeChips";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import { useLastNameFirst } from "../../hooks/useNameFormat";
import { formatNurseName, isNurseActiveInDepartment } from "../../utils/formatters";
import type { Nurse } from "../../utils/models";
import { useFetchMasterSchedule } from "../MasterSchedule/v2/hooks";
import { headerCardStyle, headingSx, rootCardStyle } from "../MasterSchedule/v2/utils";
import { HubSpotUploadModal } from "./HubSpotUploadModal";
import { SynergyStaffingGrid } from "./SynergyStaffingGrid";
import { useSynergyStaffingState } from "./useSynergyStaffingState";
import { getUniqueSections, topFiltersBarHeight } from "./utils";

export const SynergyModeStaffing: FC = () => {
	const { isAutoScheduler, getEarliestShiftDate, hospitalData } = useHospitalData();
	const startingDate = useMemo(() => getEarliestShiftDate() || DateTime.now(), [getEarliestShiftDate]);
	const { weekIndex } = useWeekIndex();
	const {
		gridData,
		weekStartDate,
		collapsedSections,
		handleSectionCollapseToggle,
		chosenDepartment,
		handleDepartmentFilterChange,
		handleToggleDrawer,
		drawerOpen,
		notification,
		timeOffRequests,
		handleRefetchDayOffRequests,
		refetchDayOffRequestsLoading,
	} = useSynergyStaffingState();

	const { track } = useAnalytics();
	const { t } = useTranslation();
	const lastNameFirst = useLastNameFirst();
	const [hubSpotModalOpen, setHubSpotModalOpen] = useState(false);
	const [selectedStaff, setSelectedStaff] = useState<SystemAutoCompleteOption<Nurse> | null>(null);

	// Fetch recurring shifts for the current week
	const { data: recurringShifts, refetch: refetchRecurringShifts } = useFetchMasterSchedule({
		startDate: weekStartDate,
	});

	// Get unique sections from locations
	const uniqueSections = useMemo(() => getUniqueSections(chosenDepartment.locations), [chosenDepartment.locations]);

	const nurseOptions = useMemo<SystemAutoCompleteOption<Nurse>[]>(
		() =>
			hospitalData.nurses
				.filter((nurse) => isNurseActiveInDepartment(nurse, chosenDepartment.department_id))
				.map((nurse) => ({
					label: formatNurseName(nurse, lastNameFirst),
					value: nurse,
				})),
		[hospitalData.nurses, chosenDepartment.department_id, lastNameFirst],
	);

	useEffect(() => {
		track("synergy_staffing_page_viewed");
	}, [track]);

	return (
		<Box sx={rootCardStyle} data-testid={"synergy-mode-staffing-root"}>
			<Box sx={[headerCardStyle, { justifyContent: "space-between" }] as SxProps<Theme>}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "start",
						gap: 2,
					}}
				>
					<Typography sx={headingSx}>Synergy Staffing</Typography>
					<DateSlider fromWeeklyStaffing={true} />
				</Box>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 2,
					}}
				>
					<Button
						variant="outlined"
						startIcon={<UploadFileIcon />}
						onClick={() => {
							track("hubspot_upload_modal_opened");
							setHubSpotModalOpen(true);
						}}
						sx={{ color: "primary.main", borderColor: "primary.main", "&:hover": { borderColor: "primary.dark" } }}
					>
						{t("hubspot.upload.credentials.button")}
					</Button>
					<DrawerTriggerButton open={drawerOpen} notification={notification} onClick={handleToggleDrawer} />
				</Box>
			</Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: 2, minHeight: topFiltersBarHeight }}>
				<DepartmentTypeChips onSelectedTypeFiltersChanged={handleDepartmentFilterChange} />
				<StaffAutoComplete nurses={nurseOptions} value={selectedStaff} onChange={(_, val) => setSelectedStaff(val)} width="200px" />
			</Box>
			<motion.div
				style={{
					flex: 1,
					overflow: "auto",
				}}
				animate={{
					width: drawerOpen ? "calc(100% - 450px)" : "100%",
				}}
				transition={{
					duration: 0.3,
					ease: "easeInOut",
				}}
			>
				<SynergyStaffingGrid
					gridData={gridData}
					weekStartDate={weekStartDate}
					collapsedSections={collapsedSections}
					handleSectionCollapseToggle={handleSectionCollapseToggle}
					recurringShifts={recurringShifts}
					refreshRecurringShifts={async () => {
						await refetchRecurringShifts();
					}}
					uniqueSections={uniqueSections}
					timeOffRequests={timeOffRequests}
					refetchDayOffRequests={handleRefetchDayOffRequests}
					refetchDayOffRequestsLoading={refetchDayOffRequestsLoading}
					selectedNurseId={selectedStaff?.value.id ?? null}
				/>
			</motion.div>
			{drawerOpen && (
				<RecommendationsDrawer
					open={drawerOpen}
					handleOpenDialog={() => {}}
					isAutoScheduler={isAutoScheduler}
					recommendations={[]}
					handleDrawerClose={handleToggleDrawer}
					setChosenLevel={(_) => {}}
					implementedChanges={{}}
					setImplementedChanges={(_) => {}}
					weekIndex={weekIndex}
					startingDate={startingDate}
					handleRemoveRecommendation={(_) => {}}
				/>
			)}
			<HubSpotUploadModal open={hubSpotModalOpen} onClose={() => setHubSpotModalOpen(false)} />
		</Box>
	);
};
